import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t, formatShortDate } from "../shared/utils"

export default class extends Controller {
  static targets = ["content"]
  static values = { editPath: String, announcementsPath: String, clubBasePath: String }

  connect() {
    loadTranslations()
    this._fetchProfile()
  }

  async _fetchProfile() {
    const data = await apiGet("/api/v1/me/profile")
    if (!data) return

    const p = data.data || data
    this.contentTarget.innerHTML = this._render(p)
  }

  _render(p) {
    const name = escapeHtml(p.full_name)
    const initials = escapeHtml(p.initials || "")

    const avatarHtml = p.avatar_url
      ? `<img src="${escapeHtml(p.avatar_url)}" alt="${escapeHtml(p.first_name)}" class="rounded-circle" style="width: 140px; height: 140px; object-fit: cover;">`
      : `<div class="d-inline-flex align-items-center justify-content-center rounded-circle bg-light" style="width: 140px; height: 140px;"><span class="fs-1 fw-bold text-muted">${initials}</span></div>`

    let publicBadge = ""
    if (p.public_profile) {
      publicBadge = `<span class="ms-2 text-danger" title="${t("users.public_profile.badge_title")}" data-bs-toggle="tooltip"><i class="fa-solid fa-circle-check"></i>&nbsp;</span>`
    }

    let sexIcon = ""
    if (p.sex != null) {
      const isMale = p.sex === 1
      sexIcon = `<span class="ms-2" title="${t(isMale ? "enums.user.sex.male" : "enums.user.sex.female")}"><i class="fa-solid ${isMale ? "fa-mars text-primary" : "fa-venus text-danger"}"></i></span>`
    }

    const clubHtml = p.clubs?.length
      ? `<p class="text-muted small mb-2"><i class="fa-solid fa-building me-1"></i>${escapeHtml(p.clubs[0].name)}</p>`
      : ""

    let infoItems = ""
    if (p.phone) infoItems += this._infoItem("fa-phone", escapeHtml(p.phone))
    if (p.birthday) {
      const ageStr = p.age ? ` (${p.age} ${t("profile.years_old")})` : ""
      infoItems += this._infoItem("fa-cake", `${formatShortDate(p.birthday)}${ageStr}`)
    }

    let locationParts = []
    if (p.city) locationParts.push(escapeHtml(p.city))
    if (p.voivodeship) locationParts.push(t(`voivodeships.${p.voivodeship}`))
    if (locationParts.length) infoItems += this._infoItem("fa-location-dot", locationParts.join(", "))

    let physParts = []
    if (p.height_cm) physParts.push(`${p.height_cm} cm`)
    if (p.weight_kg) physParts.push(`${p.weight_kg} kg`)
    if (physParts.length) infoItems += this._infoItem("fa-ruler", physParts.join(" &bull; "))

    if (p.dance_classes && (p.dance_classes.st || p.dance_classes.la)) {
      let badges = ""
      if (p.dance_classes.st) badges += `<span class="badge bg-dm-navy me-1">ST: ${escapeHtml(p.dance_classes.st)}</span>`
      if (p.dance_classes.la) badges += `<span class="badge bg-dm-navy">LA: ${escapeHtml(p.dance_classes.la)}</span>`
      infoItems += `<li class="mb-2"><i class="fa-solid fa-trophy text-muted me-2" style="width: 1.2em;"></i>${badges}</li>`
    }

    const bioCard = p.bio
      ? `<div class="card shadow-sm mb-4"><div class="card-body"><h5 class="card-title mb-3"><i class="fa-solid fa-quote-left me-2"></i>${t("profile.about_me")}</h5><p class="mb-0">${escapeHtml(p.bio)}</p></div></div>`
      : ""

    let skillsContent = ""
    if (p.dance_skills?.length) {
      const items = p.dance_skills.map(s => {
        const level = s.level ? `<span class="text-muted small ms-1">(${escapeHtml(s.level)})</span>` : ""
        return `<div class="col-sm-6 col-md-4"><div class="d-flex align-items-center gap-2 p-2 bg-light rounded"><i class="fa-solid fa-circle-check text-success"></i><div><span class="fw-medium">${escapeHtml(s.name)}</span>${level}</div></div></div>`
      }).join("")
      skillsContent = `<div class="row g-2">${items}</div>`
    } else {
      skillsContent = `<p class="text-muted mb-0">${t("dashboard.no_dance_skills")}</p>`
    }

    let clubsSidebar = ""
    if (p.memberships?.length) {
      const badges = p.memberships.map(m => {
        if (m.has_subscription) {
          return `<a href="${this.clubBasePathValue}/${escapeHtml(m.club_slug)}" class="badge bg-success text-decoration-none">${escapeHtml(m.club_name)}</a>`
        }
        return `<span class="badge bg-secondary">${escapeHtml(m.club_name)}</span>`
      }).join(" ")
      clubsSidebar = `<div class="card shadow-sm mb-4"><div class="card-body"><h5 class="card-title mb-3"><i class="fa-solid fa-building me-2"></i>${t("profile.my_clubs")}</h5><div class="d-flex flex-wrap gap-2">${badges}</div></div></div>`
    }

    let partnershipsCard = ""
    if (p.partnerships?.length) {
      const items = p.partnerships.map(pr => {
        const styles = (pr.dance_styles || []).map(s => `<span class="badge bg-secondary me-1">${escapeHtml(s)}</span>`).join("")
        return `
          <li class="list-group-item d-flex justify-content-between align-items-center px-0">
            <span>${escapeHtml(pr.partner_name || t("profile.unknown_partner"))}</span>
            <div>${styles}</div>
          </li>`
      }).join("")
      partnershipsCard = `
        <div class="card shadow-sm mb-4"><div class="card-body">
          <h5 class="card-title mb-3"><i class="fa-solid fa-user-group me-2"></i>${t("profile.partnerships")}</h5>
          <ul class="list-group list-group-flush">${items}</ul>
        </div></div>`
    }

    const competitionCard = this._renderCompetitionHistory(p.competition_entries)
    const judgingCard = this._renderJudgingHistory(p.judging_records)

    return `
      <div class="row pt-4">
        <div class="col-lg-9 col-12">
          <div class="row">
            <div class="col-md-6 col-12 mb-4">
              <div class="card shadow-sm h-100">
                <div class="card-body text-center py-4">
                  <div class="mb-3">${avatarHtml}</div>
                  <h4 class="mb-1">${publicBadge}${name}${sexIcon}</h4>
                  <p class="text-muted small mb-2">${escapeHtml(p.email)}</p>
                  ${clubHtml}
                </div>
              </div>
            </div>
            <div class="col-md-6 col-12 mb-4">
              <div class="card shadow-sm h-100">
                <div class="card-body">
                  <h5 class="card-title mb-3"><i class="fa-solid fa-user me-2"></i>${t("profile.basic_info")}</h5>
                  <ul class="list-unstyled mb-0">${infoItems}</ul>
                </div>
              </div>
            </div>
          </div>
          ${bioCard}
          <div class="card shadow-sm mb-4"><div class="card-body">
            <h5 class="card-title mb-3"><i class="fa-solid fa-music me-2"></i>${t("profile.dance_skills")}</h5>
            ${skillsContent}
          </div></div>
          ${partnershipsCard}
        </div>
        <div class="col-lg-3 col-12">
          <a href="${this.editPathValue}" class="default-btn w-100 mb-4"><i class="fa-solid fa-pen me-2"></i>${t("profile.edit_profile")}</a>
          <a href="${this.announcementsPathValue}" class="btn btn-outline-secondary w-100 mb-4"><i class="fa-solid fa-bullhorn me-2"></i>${t("nav.my_announcements")}</a>
          ${clubsSidebar}
        </div>
      </div>
      ${competitionCard}${judgingCard}`
  }

  _renderCompetitionHistory(entries) {
    if (!entries?.length) return ""

    const rows = entries.map(e => {
      const city = e.city ? ` <span class="text-muted">(${escapeHtml(e.city)})</span>` : ""
      const nameHtml = e.tournament_slug
        ? `<a href="/tournaments/${escapeHtml(e.tournament_slug)}">${escapeHtml(e.name || "")}</a>`
        : escapeHtml(e.name || "")
      return `<tr>
        <td class="text-nowrap">${formatShortDate(e.date)}</td>
        <td>${escapeHtml(e.style || "")}</td>
        <td>${nameHtml}${city}</td>
        <td>${escapeHtml(e.age_category || "")}</td>
        <td>${escapeHtml(e.position || "")}</td>
      </tr>`
    }).join("")

    return `
      <div class="card shadow-sm mb-4"><div class="card-body">
        <h5 class="card-title mb-3 d-flex align-items-center justify-content-between">
          <span><i class="fa-solid fa-trophy me-2"></i>${t("profile.competition_history")} (${entries.length})</span>
        </h5>
        <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
          <table class="table table-sm table-striped table-hover mb-0">
            <thead class="table-light"><tr>
              <th>${t("profile.date")}</th>
              <th>${t("profile.style")}</th>
              <th>${t("profile.tournament")}</th>
              <th>${t("profile.place")}</th>
              <th>${t("profile.position")}</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div></div>`
  }

  _renderJudgingHistory(records) {
    if (!records?.length) return ""

    const rows = records.map(r => {
      const nameHtml = r.tournament_slug
        ? `<a href="/tournaments/${escapeHtml(r.tournament_slug)}">${escapeHtml(r.name || "")}</a>`
        : escapeHtml(r.name || "")
      return `<tr>
        <td class="text-nowrap">${formatShortDate(r.date)}</td>
        <td>${nameHtml}</td>
        <td>${escapeHtml(r.city || "")}</td>
        <td>${escapeHtml(r.role || "")}</td>
      </tr>`
    }).join("")

    return `
      <div class="card shadow-sm mb-4"><div class="card-body">
        <h5 class="card-title mb-3 d-flex align-items-center justify-content-between">
          <span><i class="fa-solid fa-gavel me-2"></i>${t("profile.judging_history")} (${records.length})</span>
        </h5>
        <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
          <table class="table table-sm table-striped table-hover mb-0">
            <thead class="table-light"><tr>
              <th>${t("profile.date")}</th>
              <th>${t("profile.tournament")}</th>
              <th>${t("profile.place")}</th>
              <th>${t("profile.role")}</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div></div>`
  }

  _infoItem(icon, text) {
    return `<li class="mb-2"><i class="fa-solid ${icon} text-muted me-2" style="width: 1.2em;"></i>${text}</li>`
  }
}
